import React, { createContext, useContext, useEffect, useState } from "react";

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function findFirstValue(source, keys) {
  if (!source || typeof source !== "object") return "";

  for (const key of keys) {
    const value = source[key];

    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }

  return "";
}

function normalizeUserFromTestData(testData, fallbackUser = {}) {
  const source =
    testData?.user ||
    testData?.profile ||
    testData?.employee ||
    testData?.currentUser ||
    testData ||
    {};

  const id = findFirstValue(source, [
    "id",
    "userId",
    "employeeId",
    "uid",
    "guid",
    "uuid",
    "ref",
    "Ref_Key",
    "ref_key",
    "userRef",
    "employeeRef",
  ]);

  const name = findFirstValue(source, [
    "name",
    "fullName",
    "fio",
    "username",
    "login",
    "userName",
  ]);

  const fallbackName =
    fallbackUser?.name ||
    fallbackUser?.username ||
    fallbackUser?.login ||
    "Пользователь";

  const rights =
    testData?.rights ||
    source?.rights ||
    fallbackUser?.rights ||
    {};

  return {
    ...fallbackUser,
    ...source,
    id,
    name: name || fallbackName,
    username: source?.username || fallbackUser?.username || name || fallbackName,
    rights,
  };
}

function saveUserData(user) {
  if (!user) {
    localStorage.removeItem("userProfile");
    localStorage.removeItem("userRights");
    return;
  }

  localStorage.setItem("userProfile", JSON.stringify(user));

  if (user.rights) {
    localStorage.setItem("userRights", JSON.stringify(user.rights));
  } else {
    localStorage.removeItem("userRights");
  }
}

async function loadUserDataFromServer(fallbackUser = {}) {
  const response = await fetch("/api/dashboard/test-data", {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Ошибка загрузки данных пользователя: ${response.status}`);
  }

  const testData = await response.json().catch(() => ({}));
  return normalizeUserFromTestData(testData, fallbackUser);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Проверка сессии при старте (cookie -> сервер)
  useEffect(() => {
    let mounted = true;

    const checkSession = async () => {
      try {
        setLoading(true);

        const res = await fetch("/api/auth/me", {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        if (!mounted) return;

        if (!res.ok) {
          setCurrentUser(null);
          return;
        }

        const data = await res.json().catch(() => ({}));

        const fallbackUser =
          data.user || {
            username: data.username || "user",
            rights: data.rights || {},
          };

        try {
          const userFromServer = await loadUserDataFromServer(fallbackUser);

          if (!mounted) return;

          saveUserData(userFromServer);
          setCurrentUser(userFromServer);
        } catch (err) {
          console.error("Ошибка загрузки userProfile из 1С:", err);

          const fallbackWithRights = {
            ...fallbackUser,
            rights: data.rights || fallbackUser.rights || {},
          };

          saveUserData(fallbackWithRights);
          setCurrentUser(fallbackWithRights);
        }
      } catch {
        if (!mounted) return;
        setCurrentUser(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    checkSession();
    return () => {
      mounted = false;
    };
  }, []);

  const login = async (username, password) => {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ username, password }),
    });

    if (!res.ok) {
      throw new Error("Ошибка входа");
    }

    const data = await res.json().catch(() => ({}));

    const fallbackUser =
      data.user || {
        username,
        name: username,
        rights: data.rights || {},
      };

    try {
      const userFromServer = await loadUserDataFromServer(fallbackUser);

      saveUserData(userFromServer);
      setCurrentUser(userFromServer);
    } catch (err) {
      console.error("Ошибка загрузки userProfile из 1С:", err);

      const fallbackWithRights = {
        ...fallbackUser,
        rights: data.rights || fallbackUser.rights || {},
      };

      saveUserData(fallbackWithRights);
      setCurrentUser(fallbackWithRights);
    }

    return true;
  };

  const logout = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // ignore
    }

    localStorage.removeItem("userRights");
    localStorage.removeItem("userProfile");
    localStorage.removeItem("userPhoto");
    localStorage.removeItem("eventsData");

    setCurrentUser(null);
    return true;
  };

  return (
    <AuthContext.Provider value={{ currentUser, login, logout, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}